import { cloneElement, isValidElement, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn, faGear } from "@fortawesome/free-solid-svg-icons";
import { apiFetch } from "../../../api/apiClient";

export default function MetricChartPanel({
  id = "",
  title = "",
  headerExtra = null,
  dataEndpoint = "",
  chart = null,
  dataMetrics = false,
  onTooltip,
}) {
  const [panelData, setPanelData] = useState({ results: [], metrics: [] });

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!dataEndpoint) {
        setPanelData({ results: [], metrics: [] });
        return;
      }

      try {
        const url = new URL(dataEndpoint, window.location.origin);
        if (dataMetrics) {
          url.searchParams.set("metrics", "1");
        }
        const payload = await apiFetch(`${url.pathname}${url.search}`);
        if (cancelled) return;
        const results = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
        const metrics = Array.isArray(payload?.metrics) ? payload.metrics : [];
        setPanelData({ results, metrics });
      } catch (error) {
        console.warn("Metric chart panel API fetch failed:", error);
        if (!cancelled) {
          setPanelData({ results: [], metrics: [] });
        }
      }
    };
    loadData();

    return () => {
      cancelled = true;
    };
  }, [dataEndpoint, dataMetrics]);

  const renderedMetrics = useMemo(() => {
    return panelData.metrics
      .map((metric, index) => {
        const label = metric?.label;
        const value = metric?.value;
        const unit = metric?.unit;
        if (label == null || value == null) return null;
        return (
          <div className="d-flex flex-row align-items-center gap-2 justify-content-between" key={`${label}-${index}`}>
            <strong>{label}</strong> {value}{unit ? <>&nbsp;{unit}</> : null}
          </div>
        );
      })
      .filter(Boolean);
  }, [panelData.metrics]);

  const chartData = useMemo(() => {
    if (!Array.isArray(panelData.results)) return [];
    return panelData.results.map((row) => ({
      ...row,
      date: row?.date ?? row?.month,
      actual: row?.actual ?? row?.left,
      predicted: row?.predicted ?? row?.right,
      peak: row?.peak ?? row?.power,
    }));
  }, [panelData.results]);

  const chartNode = useMemo(() => {
    if (isValidElement(chart)) {
      return cloneElement(chart, { data: chartData, onTooltip });
    }
    if (typeof chart === "function") {
      return chart({ data: chartData, onTooltip });
    }
    return null;
  }, [chart, chartData, onTooltip]);

  return (
    <div id={id} className="panel p-3 mb-5">
      <div className="header d-flex justify-content-between pb-3 mb-3">
        <div className="left d-flex gap-3 align-items-center">
          <span><FontAwesomeIcon icon={faChartColumn} /> {title}</span>
          {headerExtra}
        </div>
        <div className="right d-flex align-items-stretch gap-2">
          <button><FontAwesomeIcon icon={faGear} /></button>
        </div>
      </div>
      <div className="d-flex align-items-center gap-5">
        <div className="col-8">
          {chartNode}
        </div>
        <div className="col">
          <div className="metricChartMetrics">
            {renderedMetrics}
          </div>
        </div>
      </div>
    </div>
  );
}
